/**
 * Custom Rugs Dynamic Pricing System
 * Calculates price based on dimensions entered by customer
 * Integrates with Easify Product Options app for pricing configuration
 */

document.addEventListener("DOMContentLoaded", () => {
    let priceHidden;
    let widthFeet;
    let widthInches;
    let lengthFeet;
    let lengthInches;
    let pricePerSqFt = 0; // Will be loaded from Easify app or fallback

    const targetNode = document.body;
    const config = { childList: true, subtree: true };

    const callback = (mutationsList, observer) => {
        for (let mutation of mutationsList) {
            if (mutation.type === "childList") {
                priceHidden = document.querySelector('input[name="properties[Price]"]');

                if (priceHidden) {
                    widthFeet = document.querySelector('input[name="properties[Width-Feet]"]');
                    widthInches = document.querySelector('input[name="properties[Width-Inches]"]');
                    lengthFeet = document.querySelector('input[name="properties[Length-Feet]"]');
                    lengthInches = document.querySelector('input[name="properties[Length-Inches]"]');

                    if (widthFeet && widthInches && lengthFeet && lengthInches) {
                        console.log("✅ Custom rug input fields found");

                        // Add event listeners
                        [widthFeet, widthInches, lengthFeet, lengthInches].forEach((input) => {
                            input.addEventListener("keydown", validateIntegerInput);
                            input.addEventListener("input", calculatePrice);
                        });

                        // Load price configuration
                        loadPriceConfiguration();
                        
                        // Initial calculation
                        calculatePrice();

                        observer.disconnect();
                        break;
                    }
                }
            }
        }
    };

    const observer = new MutationObserver(callback);
    observer.observe(targetNode, config);

    // Disconnect observer after 15 seconds
    setTimeout(() => {
        observer.disconnect();
        if (!priceHidden) {
            console.warn("⚠️ Custom rug pricing elements not found within 15 seconds");
        }
    }, 15000);

    /**
     * Load price configuration from Easify Product Options app
     * Falls back to variant price if app data not available
     */
    function loadPriceConfiguration() {
        console.log("🔍 Loading price configuration...");
        
        // Attempt 1: Get from Easify Product Options app
        if (window.TPOConfigs && window.TPOConfigs.product_options_easify) {
            const productOptions = window.TPOConfigs.product_options_easify;
            const productId = getProductId();
            
            if (productId) {
                const productGid = `gid://shopify/Product/${productId}`;
                
                const correctOptionSet = productOptions.find(productOption =>
                    productOption.option_set.option_set_products.some(product =>
                        product.product_id === productGid
                    )
                );
                
                if (correctOptionSet) {
                    const priceOption = correctOptionSet.option_set.options.find(option =>
                        option.option_identifier === "Price"
                    );
                    
                    if (priceOption && priceOption.additional_price) {
                        pricePerSqFt = priceOption.additional_price;
                        console.log(`✅ Price loaded from Easify app: $${pricePerSqFt}/sq ft`);
                        return;
                    }
                }
            }
        }
        
        // Attempt 2: Get from product metafields (if configured)
        if (window.meta && window.meta.product && window.meta.product.metafields) {
            const customPriceMetafield = window.meta.product.metafields.custom_price_per_sqft || 
                                        window.meta.product.metafields.price_per_sqft;
            if (customPriceMetafield) {
                pricePerSqFt = parseFloat(customPriceMetafield);
                console.log(`✅ Price loaded from metafields: $${pricePerSqFt}/sq ft`);
                return;
            }
        }
        
        // Attempt 3: Calculate from variant price (assumes variant price is for 1 sq ft)
        const variantPrice = getVariantPrice();
        if (variantPrice && variantPrice > 0) {
            pricePerSqFt = variantPrice;
            console.log(`✅ Price calculated from variant: $${pricePerSqFt}/sq ft`);
            return;
        }
        
        // Fallback: Use reasonable default (will log warning)
        pricePerSqFt = 2.50;
        console.warn(`⚠️ Price configuration not found. Using fallback: $${pricePerSqFt}/sq ft`);
        console.warn("💡 Tip: Configure price in Easify app or product metafields for accurate pricing");
    }

    /**
     * Get product ID from multiple possible sources
     */
    function getProductId() {
        // Try window.meta first (theme standard)
        if (window.meta && window.meta.product && window.meta.product.id) {
            return window.meta.product.id;
        }
        
        // Try from URL
        const urlMatch = window.location.pathname.match(/\/products\/[\w-]+/);
        if (urlMatch) {
            const productHandle = urlMatch[0].replace('/products/', '');
            // We can't get ID from handle client-side without API call, so return null
            console.log("Product handle found:", productHandle);
        }
        
        // Try from page data
        const productJsonElement = document.querySelector('[data-product-id]');
        if (productJsonElement) {
            return productJsonElement.dataset.productId;
        }
        
        return null;
    }

    /**
     * Get current variant price
     */
    function getVariantPrice() {
        // Try from price display element
        const priceElement = document.querySelector('.price__regular');
        if (priceElement) {
            const priceText = getTextContentByElement(priceElement);
            const price = parseFloat(priceText.replace(/[^\d.-]/g, ''));
            if (price && price > 0) {
                return price;
            }
        }
        
        // Try from variant select
        const variantSelect = document.querySelector('select[name="id"]');
        if (variantSelect && variantSelect.selectedOptions[0]) {
            const option = variantSelect.selectedOptions[0];
            const priceAttr = option.getAttribute('data-price');
            if (priceAttr) {
                return parseFloat(priceAttr) / 100; // Shopify returns price in cents
            }
        }
        
        return 0;
    }

    /**
     * Calculate total price based on dimensions
     */
    const calculatePrice = () => {
        try {
            if (!widthFeet || !widthInches || !lengthFeet || !lengthInches) {
                console.warn("⚠️ Input fields not ready");
                return;
            }

            // Get dimension values
            const widthFeetValue = parseFloat(widthFeet.value) || 0;
            const widthInchesValue = parseFloat(widthInches.value) || 0;
            const lengthFeetValue = parseFloat(lengthFeet.value) || 0;
            const lengthInchesValue = parseFloat(lengthInches.value) || 0;

            // Convert inches to feet
            const widthInFeet = widthFeetValue + (widthInchesValue / 12);
            const lengthInFeet = lengthFeetValue + (lengthInchesValue / 12);

            // Calculate square footage
            const squareFeet = widthInFeet * lengthInFeet;
            
            // Calculate total price
            const totalPrice = squareFeet * pricePerSqFt;
            
            console.log(`📐 Dimensions: ${widthInFeet.toFixed(2)}' × ${lengthInFeet.toFixed(2)}' = ${squareFeet.toFixed(2)} sq ft`);
            console.log(`💰 Price: ${squareFeet.toFixed(2)} sq ft × $${pricePerSqFt}/sq ft = $${totalPrice.toFixed(2)}`);

            // Validate minimum size
            if (squareFeet < 1) {
                console.log("⚠️ Dimensions too small, waiting for input...");
                updateUI(0, "Enter dimensions");
                return;
            }

            // Update UI with calculated price
            updateUI(totalPrice, null);

        } catch (error) {
            console.error("❌ Error calculating price:", error);
        }
    };

    /**
     * Update all UI elements with calculated price
     */
    function updateUI(totalPrice, statusMessage) {
        setTimeout(() => {
            // Update hidden price field for cart
            if (priceHidden) {
                priceHidden.value = `[ +$${totalPrice.toFixed(2)} ]`;
            }

            // Update visible price display
            const priceElement = document.querySelector('.price__regular');
            if (priceElement) {
                if (statusMessage) {
                    priceElement.textContent = statusMessage;
                } else {
                    priceElement.textContent = `$${totalPrice.toFixed(2)}`;
                }
            }

            // Update add to cart button text - Keep it simple (Johnny Ive style)
            const addToCartButton = document.querySelector('.product-form__submit .btn-text');
            if (addToCartButton) {
                if (statusMessage) {
                    addToCartButton.textContent = statusMessage;
                } else {
                    // Simple, clean button text - price is shown above
                    addToCartButton.textContent = "Add to Cart";
                }
            }

            // Update sticky button if exists
            const stickyButton = document.querySelector('.sticky-cta-button .sticky-cta-text');
            const stickyPrice = document.querySelector('.sticky-cta-button .sticky-cta-price');
            if (stickyButton && totalPrice > 0) {
                if (!statusMessage) {
                    stickyPrice.textContent = `$${totalPrice.toFixed(2)}`;
                }
            }

            console.log("✅ UI updated with price:", totalPrice.toFixed(2));
        }, 100);
    }

    /**
     * Validate integer input for dimension fields
     */
    const validateIntegerInput = (event) => {
        const key = event.key;
        const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'];
        
        if (!/^\d*$/.test(event.target.value + key) && !allowedKeys.includes(key)) {
            event.preventDefault();
        }
    };

    /**
     * Handle color variant changes (reload to update pricing if needed)
     */
    const variantContainer = document.querySelector("ul.swatches--variant");
    if (variantContainer) {
        variantContainer.addEventListener("click", function (event) {
            if (event.target.tagName === "INPUT") {
                console.log("🔄 Variant changed, reloading...");
                setTimeout(function () {
                    window.location.reload();
                }, 1000);
            }
        });
    }

    /**
     * Helper: Get text content from element safely
     */
    function getTextContentByElement(element) {
        if (!element) return "";
        if (element.textContent && element.textContent.trim() !== "") {
            return element.textContent.trim();
        } else if (element.value !== undefined) {
            return element.value.trim();
        } else if (element.innerText) {
            return element.innerText.trim();
        }
        return "";
    }

    // Also wait for Easify app to potentially load after DOM ready
    setTimeout(() => {
        if (pricePerSqFt === 0) {
            console.log("🔄 Checking for late-loading price configuration...");
            loadPriceConfiguration();
            if (widthFeet && lengthFeet) {
                calculatePrice();
            }
        }
    }, 2000);
});